import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Briefcase, CalendarDays, CheckCircle2, ClipboardList, Globe, MapPin, Sparkles, Tags, Target, Users, Wallet } from 'lucide-react';
import type { ChatSuggestedEvent, ChatSuggestedGoal, ChatSuggestedService } from '@/lib/api/chatCreation.service';
import type { CelebrationDraftDto } from '@om/shared-types';

interface CreationPreviewPanelProps {
  extractedFields: CelebrationDraftDto;
  confirmedTags: Record<string, string[]>;
  selectedEvents: ChatSuggestedEvent[];
  selectedActivities: Record<string, string[]>;
  goalSuggestions: Record<string, ChatSuggestedGoal[]>;
  selectedGoalTemplateIdsByEvent: Record<string, number[]>;
  serviceSuggestions: ChatSuggestedService[];
  selectedServiceDefinitionIds: number[];
  missingFields: string[];
  workflowStatus?: string;
  collapsed?: boolean;
}

const toNames = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(item => String(item)).filter(Boolean);
  }
  return [];
};

const MISSING_FIELD_LABELS: Record<string, string> = {
  _celebrant_role: 'Celebrant role',
  _celebrant_email: 'Celebrant email',
  celebration_type: 'Celebration type',
  celebration_subtype: 'Celebration subtype',
  celebrant_names: 'Celebrant name',
  celebrant_roles: 'Celebrant role',
  celebrant_emails: 'Celebrant email',
  start_date: 'Start date',
  end_date: 'End date',
  location_name: 'Location',
  guest_count_estimate: 'Guest count',
  title: 'Celebration title',
  budget: 'Budget',
  religion: 'Religion',
  culture: 'Culture',
  style: 'Style',
};

const INTAKE_FIELD_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  start_date: CalendarDays,
  end_date: CalendarDays,
  location_name: MapPin,
  budget: Wallet,
  guest_count_estimate: Users,
  culture: Globe,
  religion: BookOpen,
  style: Sparkles,
};

const formatMissingFieldLabel = (field: string): string => {
  const mapped = MISSING_FIELD_LABELS[field];
  if (mapped) return mapped;

  const withoutUnderscore = field.replace(/^_+/, '');
  const spaced = withoutUnderscore
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim();

  if (!spaced) return 'Required detail';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const formatDateForPreview = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const datePrefixMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (datePrefixMatch) {
    const year = Number.parseInt(datePrefixMatch[1], 10);
    const month = Number.parseInt(datePrefixMatch[2], 10);
    const day = Number.parseInt(datePrefixMatch[3], 10);
    const utcDate = new Date(Date.UTC(year, month - 1, day));

    if (!Number.isNaN(utcDate.getTime())) {
      return utcDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed;
  }

  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const CreationPreviewPanel: React.FC<CreationPreviewPanelProps> = ({
  extractedFields,
  confirmedTags,
  selectedEvents,
  selectedActivities,
  goalSuggestions,
  selectedGoalTemplateIdsByEvent,
  serviceSuggestions,
  selectedServiceDefinitionIds,
  missingFields,
  workflowStatus,
  collapsed,
}) => {
  const { t } = useTranslation(['celebrations', 'common']);
  const missingFieldSet = new Set(missingFields);
  const title = typeof extractedFields.title === 'string' ? extractedFields.title : '';
  const celebrationType = typeof extractedFields.celebration_type === 'string'
    ? extractedFields.celebration_type
    : '';
  const celebrationSubtype = typeof extractedFields.celebration_subtype === 'string'
    ? extractedFields.celebration_subtype
    : '';
  const celebrants = toNames(extractedFields.celebrant_names);
  const celebrantRoles = toNames(extractedFields.celebrant_roles);
  const celebrantEmails = toNames(extractedFields.celebrant_emails);

  const startDateRaw = typeof extractedFields.start_date === 'string' ? extractedFields.start_date : '';
  const endDateRaw = typeof extractedFields.end_date === 'string' ? extractedFields.end_date : '';
  const locationNameRaw = typeof extractedFields.location_name === 'string' ? extractedFields.location_name : '';
  const cultureRaw = typeof extractedFields.culture === 'string' ? extractedFields.culture : '';
  const religionRaw = typeof extractedFields.religion === 'string' ? extractedFields.religion : '';
  const styleRaw = typeof extractedFields.style === 'string' ? extractedFields.style : '';
  const budgetRaw = typeof extractedFields.budget === 'number' ? extractedFields.budget : undefined;
  const guestCountEstimateRaw = typeof extractedFields.guest_count_estimate === 'number'
    ? extractedFields.guest_count_estimate
    : undefined;

  const startDate = missingFieldSet.has('start_date') ? '' : startDateRaw;
  const endDate = missingFieldSet.has('end_date') ? '' : endDateRaw;
  const locationName = missingFieldSet.has('location_name') ? '' : locationNameRaw;
  const culture = missingFieldSet.has('culture') ? '' : cultureRaw;
  const religion = missingFieldSet.has('religion') ? '' : religionRaw;
  const style = missingFieldSet.has('style') ? '' : styleRaw;
  const budget = missingFieldSet.has('budget') ? undefined : budgetRaw;
  const guestCountEstimate = missingFieldSet.has('guest_count_estimate') ? undefined : guestCountEstimateRaw;

  const intakeDetails: Array<{ field: string; value: string }> = [
    ...(startDate ? [{ field: 'start_date', value: formatDateForPreview(startDate) }] : []),
    ...(endDate ? [{ field: 'end_date', value: formatDateForPreview(endDate) }] : []),
    ...(locationName ? [{ field: 'location_name', value: locationName }] : []),
    ...(budget !== undefined ? [{ field: 'budget', value: `$${budget.toLocaleString()}` }] : []),
    ...(guestCountEstimate !== undefined ? [{ field: 'guest_count_estimate', value: `${guestCountEstimate}` }] : []),
    ...(culture ? [{ field: 'culture', value: culture }] : []),
    ...(religion ? [{ field: 'religion', value: religion }] : []),
    ...(style ? [{ field: 'style', value: style }] : []),
  ];

  const awaitingTagConfirmation = ['awaiting_intake_confirmation', 'awaiting_tag_review'].includes(workflowStatus ?? '');
  const awaitingEventConfirmation = ['awaiting_intake_confirmation', 'awaiting_tag_review', 'awaiting_event_review', 'awaiting_activity_review'].includes(workflowStatus ?? '');
  const hasBlockingMissingFields = missingFields.length > 0;
  const isReady = !!title && !!celebrationType && !hasBlockingMissingFields;

  const workflowProgressMap: Record<string, number> = {
    start: 10,
    collecting_details: 30,
    awaiting_intake_confirmation: 45,
    awaiting_tag_review: 55,
    awaiting_event_review: 70,
    awaiting_activity_review: 85,
    awaiting_goal_review: 88,
    awaiting_plan_structure_preference: 90,
    awaiting_service_preference: 92,
    awaiting_service_review: 94,
    awaiting_create_action: 95,
    ready_to_create: 100,
    completed: 100,
    celebration_created: 100,
  };
  const workflowProgress = workflowStatus ? (workflowProgressMap[workflowStatus] ?? 40) : 10;
  const progressValue = isReady ? 100 : workflowProgress;
  // Only show events that the user has explicitly confirmed (not pre-selected defaults during review)
  const previewEvents = selectedEvents;

  // Goals: flatten selected goal titles across all events
  const selectedGoalTitles: { eventId: string; title: string }[] = [];
  for (const [eventId, selectedIds] of Object.entries(selectedGoalTemplateIdsByEvent)) {
    const eventGoals = goalSuggestions[eventId] ?? [];
    for (const goal of eventGoals) {
      if (selectedIds.includes(goal.goalTemplateId)) {
        selectedGoalTitles.push({ eventId, title: goal.title });
      }
    }
  }
  const hasGoalSuggestions = Object.values(goalSuggestions).some(g => g.length > 0);
  const awaitingGoalConfirmation = workflowStatus === 'awaiting_goal_review';

  // Services: selected service objects
  const selectedServiceIdSet = new Set(selectedServiceDefinitionIds);
  const selectedServices = serviceSuggestions.filter(s => selectedServiceIdSet.has(s.serviceDefinitionId));
  const hasServiceSuggestions = serviceSuggestions.length > 0;
  const awaitingServiceConfirmation = ['awaiting_service_preference', 'awaiting_service_review'].includes(workflowStatus ?? '');

  const selectedActivityCount = Object.values(selectedActivities).reduce((sum, values) => sum + values.length, 0);
  const tagEntries = Object.entries(confirmedTags)
    .map(([category, values]) => ({
      category,
      values: values.filter(Boolean),
    }))
    .filter(entry => entry.values.length > 0);
  const totalSelectedTags = tagEntries.reduce((sum, entry) => sum + entry.values.length, 0);

  const categoryBadgeClasses = [
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
    'bg-primary/15 text-primary border-primary/30',
  ] as const;

  const combinedTags = tagEntries.flatMap((entry, categoryIndex) =>
    entry.values.map(value => ({
      value,
      category: entry.category,
      className: categoryBadgeClasses[Math.min(categoryIndex, categoryBadgeClasses.length - 1)],
    })),
  );

  if (collapsed) {
    return (
      <Card className="h-full lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col min-h-0">
        <CardHeader className="pb-3 bg-primary/5 border-b border-primary/10">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            {t('celebrations:chat.creationPreview.title')}
          </CardTitle>
        </CardHeader>
        <div className="px-6 pb-3 space-y-2 border-b">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('celebrations:chat.creationPreview.statusLabel')}</span>
            <Badge variant={isReady ? 'default' : 'secondary'} className={isReady ? 'bg-emerald-600 text-white border-emerald-600' : ''}>
              {isReady ? t('celebrations:chat.creationPreview.ready') : t('celebrations:chat.creationPreview.partial')}
            </Badge>
          </div>
          <Progress value={progressValue} className="h-2" />
        </div>
        <CardContent className="text-sm text-muted-foreground space-y-4 overflow-y-scroll min-h-0">
          <p>{t('celebrations:chat.creationPreview.collapsed.startChatting')}</p>
          <p className="text-xs">{t('celebrations:chat.creationPreview.collapsed.completeForReady')}</p>
        </CardContent>
      </Card>
    );
  }

  const missingFieldLabels = Array.from(
    new Set(
      missingFields
        .map(formatMissingFieldLabel)
        .filter(Boolean),
    ),
  );

  return (
    <Card className="h-full lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col min-h-0">
      <CardHeader className="pb-3 bg-primary/5 border-b border-primary/10">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          {t('celebrations:chat.creationPreview.title')}
        </CardTitle>
      </CardHeader>
      <div className="px-6 pb-3 space-y-2 border-b">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('celebrations:chat.creationPreview.statusLabel')}</span>
            <Badge variant={isReady ? 'default' : 'secondary'} className={isReady ? 'bg-emerald-600 text-white border-emerald-600' : ''}>
              {isReady ? t('celebrations:chat.creationPreview.ready') : t('celebrations:chat.creationPreview.partial')}
            </Badge>
          </div>
        <Progress value={progressValue} className="h-2" />
        {missingFieldLabels.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('celebrations:chat.creationPreview.stillNeeded', { fields: missingFieldLabels.join(', ') })}
          </p>
        )}
      </div>

      <CardContent className="space-y-4 text-sm overflow-y-scroll pr-1 min-h-0 flex-1">

        <div className="border-b pb-4">
          <p className="text-xs text-muted-foreground mb-1">{t('celebrations:chat.creationPreview.sections.celebration')}</p>
          <p className="font-medium break-words">{title || t('celebrations:chat.creationPreview.pendingTitle')}</p>
          <p className="text-muted-foreground break-words">
            {celebrationType || t('celebrations:chat.creationPreview.pendingType')}
            {celebrationSubtype ? ` - ${celebrationSubtype}` : ''}
          </p>
          {intakeDetails.length > 0 && (
            <div className="mt-3 rounded-md border bg-muted/20 p-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
              {intakeDetails.map(({ field, value }) => {
                const Icon = INTAKE_FIELD_ICONS[field] ?? CalendarDays;
                return (
                  <div key={field} className="flex items-start gap-1.5 min-w-0">
                    <Icon className="h-3 w-3 text-primary/70 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">{MISSING_FIELD_LABELS[field] ?? field}</p>
                      <p className="text-xs font-medium text-foreground break-words leading-snug mt-0.5">{value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-b pb-4">
          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {t('celebrations:chat.creationPreview.sections.celebrants')}
          </p>
          {celebrants.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {celebrants.map((name, index) => {
                const role = celebrantRoles[index];
                const email = celebrantEmails[index];
                const details = [role, email].filter(Boolean).join(' · ');
                return (
                  <Badge key={`${name}-${index}`} variant="outline">
                    {details ? `${name} · ${details}` : name}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground">{t('common:status.pending')}</p>
          )}
        </div>

        <div className="border-b pb-4">
          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Tags className="h-3.5 w-3.5" />
            {t('celebrations:chat.creationPreview.sections.tags')}
          </p>
          {totalSelectedTags > 0 ? (
            <div className="space-y-2">
              <p>{t('celebrations:chat.creationPreview.selectedCount', { count: totalSelectedTags })}</p>
              <div className="flex flex-wrap gap-1">
                {combinedTags.map((tag, index) => (
                  <Badge
                    key={`${tag.category}-${tag.value}-${index}`}
                    variant="outline"
                    className={tag.className}
                  >
                    {tag.value}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">{awaitingTagConfirmation ? t('celebrations:chat.creationPreview.awaitingConfirmation') : t('common:status.pending')}</p>
          )}
        </div>

        <div className="border-b pb-4">
          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
            <CalendarDays className="h-3.5 w-3.5" />
            {t('celebrations:chat.creationPreview.sections.eventsActivities')}
            {previewEvents.length > 0 && (
              <span className="ml-auto font-normal">
                {t('celebrations:chat.creationPreview.eventsCount', { count: previewEvents.length })}
                {selectedActivityCount > 0 && ` · ${t('celebrations:chat.creationPreview.activitiesCount', { count: selectedActivityCount })}`}
              </span>
            )}
          </p>
          {previewEvents.length > 0 ? (
            <div className={`grid gap-2 ${previewEvents.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {previewEvents.map(event => {
                const eventActivities = selectedActivities[event.id] ?? [];
                return (
                  <div key={event.id} className="rounded-md border bg-muted/30 p-2 flex flex-col gap-1">
                    <div className="flex items-start gap-1">
                      <CalendarDays className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                      <p className="text-xs font-semibold leading-tight break-words">{event.title}</p>
                    </div>
                    {event.eventType && (
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground pl-4">{event.eventType.replace(/_/g, ' ')}</p>
                    )}
                    {eventActivities.length > 0 && (
                      <ul className="mt-1 space-y-0.5 pl-4 border-t pt-1">
                        {eventActivities.slice(0, 4).map(name => (
                          <li key={name} className="text-[11px] text-muted-foreground flex items-start gap-1">
                            <CheckCircle2 className="h-2.5 w-2.5 mt-0.5 shrink-0 text-muted-foreground/60" />
                            <span className="break-words">{name}</span>
                          </li>
                        ))}
                        {eventActivities.length > 4 && (
                          <li className="text-[11px] text-muted-foreground pl-3">{t('celebrations:chat.creationPreview.moreCount', { count: eventActivities.length - 4 })}</li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground">{awaitingEventConfirmation ? t('celebrations:chat.creationPreview.awaitingConfirmation') : t('celebrations:chat.creationPreview.noneSelectedYet')}</p>
          )}
        </div>

        <div className="border-b pb-4">
          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
            <Target className="h-3.5 w-3.5" />
            {t('celebrations:chat.creationPreview.sections.goals')}
            {selectedGoalTitles.length > 0 && (
              <span className="ml-auto font-normal">{t('celebrations:chat.creationPreview.selectedCount', { count: selectedGoalTitles.length })}</span>
            )}
          </p>
          {selectedGoalTitles.length > 0 ? (
            <div className="grid grid-cols-3 gap-1">
              {selectedGoalTitles.map((g, i) => (
                <div
                  key={`${g.eventId}-${i}`}
                  className="rounded-md border bg-muted/30 px-1.5 py-1 flex items-start gap-1 min-w-0"
                >
                  <CheckCircle2 className="h-2.5 w-2.5 mt-0.5 shrink-0 text-primary/60" />
                  <span className="text-[11px] break-words leading-tight">{g.title}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {awaitingGoalConfirmation
                ? t('celebrations:chat.creationPreview.awaitingConfirmation')
                : hasGoalSuggestions
                  ? t('celebrations:chat.creationPreview.noneSelected')
                  : t('celebrations:chat.creationPreview.nonePlanned')}
            </p>
          )}
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
            <Briefcase className="h-3.5 w-3.5" />
            {t('celebrations:chat.creationPreview.sections.services')}
            {selectedServices.length > 0 && (
              <span className="ml-auto font-normal">{t('celebrations:chat.creationPreview.selectedCount', { count: selectedServices.length })}</span>
            )}
          </p>
          {selectedServices.length > 0 ? (
            <div className="space-y-3">
              {Array.from(
                selectedServices.reduce((map, s) => {
                  const key = s.category ?? 'Other';
                  if (!map.has(key)) map.set(key, []);
                  map.get(key)!.push(s);
                  return map;
                }, new Map<string, typeof selectedServices>()),
              ).map(([category, services]) => (
                <div key={category}>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                    {category.replace(/_/g, ' ')}
                  </p>
                  <div className="grid grid-cols-3 gap-1">
                    {services.map(s => (
                      <div
                        key={s.serviceDefinitionId}
                        className="rounded-md border bg-muted/30 px-1.5 py-1 flex items-start gap-1 min-w-0"
                      >
                        <Briefcase className="h-2.5 w-2.5 mt-0.5 shrink-0 text-primary/60" />
                        <span className="text-[11px] break-words leading-tight">{s.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {awaitingServiceConfirmation
                ? t('celebrations:chat.creationPreview.awaitingConfirmation')
                : hasServiceSuggestions
                  ? t('celebrations:chat.creationPreview.noneSelected')
                  : t('celebrations:chat.creationPreview.nonePlanned')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
